// ============================================================================
// layouts.js — canonical plate layout presets (replaces the template gallery)
// ============================================================================
// Real orders are variations of a small set of arrangements (Ryan 2026-07-11).
// A preset is just a recipe that SEEDS `profile.elements`; once applied, the
// elements are the source of truth and the customer edits them individually.
//
// Each entry in a preset's `elements` is passed to createElement(type, slot,
// overrides). Slots are the ring anchors: top/bottom/left/right (mid of side).
//
// Modeled on the reference plates:
//   • Ardsley / Campbell → logo left, weight right, arch top + arch bottom
//   • ASU                → logo top, weight both sides, wordmark/arch bottom
// ============================================================================

import { deriveArcText, deriveArcTextTri, distributeAcrossArcs } from './arc-text.js';
import { createElement, DEFAULT_LAYOUT_PRESET, applyAutoStyleToElement } from './design-profile.js';
import { SMALL_GRIP_MAX_LB } from './geometry/grip-plate.js';
import { layoutFamilyFor } from './products.js';

// Seeded guide copy the presets drop into fresh arctext slots. Treated as a
// PLACEHOLDER, not real content: it must not survive a relayout as "the user's
// text" (WD-01) and the quote step warns if it's still on the plate (WD-14).
// 'GOES' and 'HERE' are the grip plate's third and fourth (2026-07-30): its Tri- layouts have THREE
// gaps, so the bumper's two-line "YOUR TEXT / GOES HERE" is split across the two lower ones. Every
// entry has to be listed separately — `isPlaceholderText` matches whole strings, and a half that
// isn't here would survive a relayout as if the buyer had typed it (WD-01) and would ship on the
// plate past the quote-step warning (WD-14).
export const PLACEHOLDER_TEXTS = Object.freeze(['YOUR TEXT', 'GOES HERE', 'GOES', 'HERE']);

/** True if `t` is one of the seeded placeholder strings (case/space-insensitive). */
export function isPlaceholderText(t) {
  return PLACEHOLDER_TEXTS.includes(String(t == null ? '' : t).trim().toUpperCase());
}

/**
 * @typedef {Object} LayoutPreset
 * @property {string} id
 * @property {string} name    - owned, buyer-facing name (e.g. "Varsity Arch"). Shown in the UI.
 * @property {string} blurb   - plain one-line descriptor of the arrangement.
 * @property {'Arched'|'Badge'|'Minimal'|'Dumbbell'|'Grip'} family - grouping for the View-all grid.
 * @property {string} label   - LEGACY descriptive string (kept for back-compat / a11y fallback).
 * @property {Array<{type:'logo'|'weight'|'arctext', slot:'top'|'bottom'|'left'|'right', overrides?:object}>} elements
 * @property {'plate'|'dumbbell'|'grip'} [product] - which LAYOUT FAMILY this preset
 *   belongs to (products.js's MODELS.layoutFamily — NOT a product key). Omitted
 *   (undefined) == 'plate' (the original 15 — a real ring/hub with arc-text
 *   following its curve). The dumbbell has no physical hub, so its family uses
 *   flatter, disc-centered arrangements (Fable §9.1); the BARBELL shares that
 *   family because its heads are literally the dumbbell's. The grip plate's face is
 *   broken by three hand cutouts, so it gets its own (below).
 */

/** The layout family a preset belongs to — undefined reads as 'plate' (the original set). */
export function layoutPresetProduct(preset) {
  return (preset && preset.product) || 'plate';
}

/**
 * A FAMILY ('plate' | 'dumbbell' | 'grip') from either a family or a product KEY.
 *
 * ⚠ THIS EXISTS BECAUSE THE DISTINCTION IS INVISIBLE AT THE CALL SITE (2026-08-05). Both are
 * lowercase strings named "product", and passing the wrong one produced no error, no warning and no
 * empty-state — just `[]`, which every caller reads as "this product has no layouts" and handles by
 * falling through to something plausible. Four of the six callers had it wrong:
 *   • `ui/project-push.js` — the DERIVED preset its own comment asks for could never resolve, so
 *     every staged design silently fell back to the STORED id (an emptied Varsity Arch stayed a
 *     Varsity Arch on the rep's tray instead of becoming the Marquee it now is).
 *   • `ui/showcase-admin.js` ×3 — worse: the publish dialog's layout `<select>` was built by mapping
 *     over that array, so it rendered with **no options at all**, and a slide could be published
 *     naming a layout nobody could see or change.
 * A product key always contains `|` (`category|kind`) and a family never does, so the two are
 * distinguishable with certainty — which makes normalising here strictly better than asking six
 * call sites to remember. Keep it: this is a class of bug that comes back.
 */
function asLayoutFamily(product) {
  if (!product) return 'plate';
  return String(product).includes('|') ? layoutFamilyFor(product) : product;
}

/** All presets for a given layout family ('plate' | 'dumbbell' | 'grip').
 *  Accepts a product KEY too (`bumper|Competition Urethane`) — see `asLayoutFamily`. */
export function layoutPresetsForProduct(product, weight) {
  const want = asLayoutFamily(product);
  return LAYOUT_PRESETS.filter((p) => {
    if (layoutPresetProduct(p) !== want) return false;
    /* DENOMINATION GATING — the grip family is two different FACES under one product.
     * The cutout plates have three slots in the gaps between hand holes; the 2.5/5 have a
     * plain annulus and four. A preset built for one is wrong on the other.
     *
     * ⚠ `weight === undefined` MUST mean "no filtering". This function has six callers and
     * only the layout picker knows the denomination — the showcase admin, project-push's
     * composition matcher and the baseline dumper all legitimately want the whole family.
     * Defaulting an absent weight to a number instead would silently hide half the presets
     * from three call sites that never asked about denominations at all. */
    if (weight == null || typeof p.weights !== 'function') return true;
    return p.weights(Number(weight));
  });
}

/** Family order for the View-all grid (Arched · Minimal · Badge — Badge last, Ryan 2026-07-22 — plus
 *  Grip, which only ever shows alongside its own product's preset list, never mixed with the
 *  bumper's three).
 *    `Dumbbell` is GONE as a grouping (2026-07-30): the cap's twelve layouts sort into the same
 *  three buckets the bumper's sixteen do, which is the picker's existing grouping doing its job
 *  rather than a fourth heading nobody needs. `Grip` stays because "the gaps between the hand
 *  cutouts" is a real arrangement with no counterpart on any other face. */
export const LAYOUT_FAMILIES = Object.freeze(['Arched', 'Minimal', 'Badge', 'Grip']);

/** Presets for a product's family, in the SAME order the View-all grid shows them —
 *  grouped by LAYOUT_FAMILIES, each family keeping its array order. The compact slider
 *  carousel and the View-all modal MUST share this so their sequences line up (Ryan
 *  2026-07-23: the raw LAYOUT_PRESETS order is money-makers-first, which interleaved
 *  families and didn't match the family-grouped View-all). Any preset whose family
 *  isn't in LAYOUT_FAMILIES is appended (never silently dropped). */
export function orderedLayoutPresetsForProduct(product, weight) {
  const presets = layoutPresetsForProduct(product, weight);
  // ── THE GROUP HOLDING THIS FAMILY'S DEFAULT LEADS (2026-07-30) ─────────────────────────────
  // `LAYOUT_FAMILIES` is one global order, and it was applied verbatim to every product — which is
  // right for the bumper by luck rather than by rule: its default IS an Arched layout, and Arched is
  // first in the list. Nothing carried that property to the others. The grip plate's default is
  // Classic Grip, and splitting its four into Grip + Arched (Ryan 2026-07-30) would have opened the
  // picker on Tri-Arch; the cap's default is Stacked, and it was ALREADY opening on Crest — a
  // regression from grouping its twelve layouts, and one nobody had noticed.
  //   "Money-makers first" is the stated rule for the preset array itself, and this is the same
  // rule for the groups: whatever a product OPENS on is what its picker should lead with. Derived
  // from `FAMILY_DEFAULT_PRESET` rather than listed per product, so the two cannot drift — moving a
  // default moves the grid with it.
  // ⚠ NORMALISE HERE TOO. `layoutPresetsForProduct` above resolves a key by itself, but this second
  // lookup is keyed straight off the argument — so a caller passing a key got the ordered list of a
  // family it did resolve, led by the default of one it did not.
  const lead = getLayoutPreset(FAMILY_DEFAULT_PRESET[asLayoutFamily(product)]);
  const leadFamily = lead && presets.includes(lead) ? lead.family : null;
  const order = leadFamily
    ? [leadFamily, ...LAYOUT_FAMILIES.filter((f) => f !== leadFamily)]
    : LAYOUT_FAMILIES;
  const grouped = order.flatMap((fam) => presets.filter((p) => p.family === fam));
  const stragglers = presets.filter((p) => !LAYOUT_FAMILIES.includes(p.family));
  return [...grouped, ...stragglers];
}

// Owned layout NAMES + families (Fable Pass-2 §2.6). Ordered money-makers-first —
// the array order IS the carousel order and the View-all grid is grouped by family
// (Arched → Badge → Minimal). The default (Varsity Arch) stays index 0. Names are a
// system Ryan can veto individually; "Design 1…15" is dead. Element recipes are
// UNCHANGED from the numbered set — only order + naming metadata was added.
/** @type {ReadonlyArray<LayoutPreset>} */
/**
 * The preset the plate ACTUALLY looks like right now, which is not always the one that was picked
 * (Ryan 2026-07-25). Empty an arc-text field on Varsity Arch and the plate becomes a Marquee — arch
 * top, logo left, weight right — because a line with no words draws nothing. The stored
 * `layoutPreset` still said Varsity Arch, so "View all layouts" highlighted a layout that wasn't on
 * screen. Type the missing line back in and it is a Varsity Arch again.
 *
 * Derived, never stored: the composition IS the truth, and re-deriving it means the two can't drift
 * and there's no migration or extra state to keep in step. Matching is by the set of pieces that
 * would render — an arc-text with no text is not one of them — against each preset's own element
 * list, so it stays correct as presets are added or changed.
 *
 * @param {object} profile
 * @param {Array} presets  the presets in scope (already narrowed to this product's family)
 * @returns {string|null} the matching preset id, or null when nothing matches exactly
 */
export function matchPresetToComposition(profile, presets) {
  const els = (profile && profile.elements) || [];
  const renders = (e) => {
    if (!e || !e.slot) return false;
    if (e.type === 'arctext') return String(e.text || '').trim() !== '';
    if (e.type === 'logo') return !!(e.logo && e.logo.dataUrl);
    return true;
  };
  // `big` is part of the identity, not a detail: "Big Badge" is logo-left + weight-right, which is
  // the SAME footprint a Varsity Arch collapses to once both its arcs are empty. Without this the
  // picker would call that plate a Big Badge, when the logo is nothing of the sort.
  //   `upsideDown` joined it on 2026-07-30, and earns its place twice over. Tri-Arch and Tri-Word
  // occupy IDENTICAL slots — an arc plus a logo/weight in all three grip gaps — and differ only in
  // whether the lower two words are flipped about their own arc. Without it, every Tri-Word plate
  // reported itself as a Tri-Arch (first match wins) and the picker ticked the wrong card while the
  // plate on screen plainly showed the other one.
  //   It also closes a PRE-EXISTING collision on the bumper: "Competition" and "Competition Mirror"
  // are both weight-left + weight-right, differing only in the right number being inverted, so the
  // Mirror could never report itself either.
  const tag = (x, isBig, flip) => `${x.type}@${x.slot}${isBig ? '!big' : ''}${flip ? '!flip' : ''}`;
  const sig = (list, bigOf, flipOf) => list
    .map((x) => tag(x, bigOf(x), flipOf(x)))
    .sort()
    .join('|');
  const mine = sig(els.filter(renders), (e) => e.big === true, (e) => e.upsideDown === true);
  if (!mine) return null;
  const hit = (presets || []).find(
    (pr) => sig(pr.elements || [], (e) => !!(e.overrides && e.overrides.big), (e) => !!(e.overrides && e.overrides.upsideDown)) === mine,
  );
  return hit ? hit.id : null;
}

export const LAYOUT_PRESETS = Object.freeze([
  // ── Arched family ──────────────────────────────────────────────────────────
  {
    id: 'logo-l-weight-r-arch-tb',
    name: 'Varsity Arch',
    blurb: 'arch top & bottom, logo left, weight right',
    family: 'Arched',
    label: 'Arch top & bottom · logo left · weight right',
    elements: [
      { type: 'arctext', slot: 'top', overrides: { text: 'YOUR TEXT' } },
      { type: 'arctext', slot: 'bottom', overrides: { text: 'GOES HERE' } },
      { type: 'logo', slot: 'left' },
      { type: 'weight', slot: 'right' },
    ],
  },
  {
    id: 'text-tb-seal-weight-lr',
    name: 'The Seal',
    blurb: 'arch top & bottom, weight both sides',
    family: 'Arched',
    label: 'Arch top & bottom (seal) · weight both sides',
    elements: [
      { type: 'arctext', slot: 'top', overrides: { text: 'YOUR TEXT' } },
      { type: 'arctext', slot: 'bottom', overrides: { text: 'GOES HERE', upsideDown: true } },
      { type: 'weight', slot: 'left' },
      { type: 'weight', slot: 'right', overrides: { upsideDown: true } },
    ],
  },
  {
    id: 'logo-l-weight-r-arch-t',
    name: 'Marquee',
    blurb: 'arch top, logo left, weight right',
    family: 'Arched',
    label: 'Arch top · logo left · weight right',
    elements: [
      { type: 'arctext', slot: 'top', overrides: { text: 'YOUR TEXT' } },
      { type: 'logo', slot: 'left' },
      { type: 'weight', slot: 'right' },
    ],
  },
  {
    id: 'text-t-weight-b',
    name: 'Headliner',
    blurb: 'arch top, weight bottom',
    family: 'Arched',
    label: 'Arch top · weight bottom',
    elements: [
      { type: 'arctext', slot: 'top', overrides: { text: 'YOUR TEXT' } },
      { type: 'weight', slot: 'bottom' },
    ],
  },
  {
    id: 'logo-top-weight-lr-arch-b',
    name: 'Letterman',
    blurb: 'logo top, weight both sides, arch bottom',
    family: 'Arched',
    label: 'Logo top · weight both sides · arch bottom',
    elements: [
      { type: 'logo', slot: 'top' },
      { type: 'weight', slot: 'left' },
      { type: 'weight', slot: 'right' },
      { type: 'arctext', slot: 'bottom', overrides: { text: 'YOUR TEXT' } },
    ],
  },
  {
    id: 'arch-top-logo-bottom-weight-lr',
    name: 'Pennant',
    blurb: 'arch top, weight both sides, logo bottom',
    family: 'Arched',
    label: 'Arch top · weight both sides · logo bottom',
    elements: [
      { type: 'arctext', slot: 'top', overrides: { text: 'YOUR TEXT' } },
      { type: 'weight', slot: 'left' },
      { type: 'weight', slot: 'right' },
      { type: 'logo', slot: 'bottom' },
    ],
  },
  {
    id: 'logo-l-weight-r-arch-b',
    name: 'Baseline',
    blurb: 'arch bottom, logo left, weight right',
    family: 'Arched',
    label: 'Arch bottom · logo left · weight right',
    elements: [
      { type: 'arctext', slot: 'bottom', overrides: { text: 'YOUR TEXT' } },
      { type: 'logo', slot: 'left' },
      { type: 'weight', slot: 'right' },
    ],
  },
  {
    id: 'text-b-weight-t',
    name: 'Kickoff',
    blurb: 'weight top, arch bottom',
    family: 'Arched',
    label: 'Weight top · arch bottom',
    elements: [
      { type: 'weight', slot: 'top' },
      { type: 'arctext', slot: 'bottom', overrides: { text: 'YOUR TEXT' } },
    ],
  },
  // ── Big-logo pair (#7/#8, Ryan 2026-07-13) → the Badge family ───────────────
  // A BIG logo filling the whole paintable ring (top→bottom + ¼" bleed) on one
  // side, clipped to ring B — only the ring takes ink (hub + rings A/C unchanged).
  // The weight number stays in its NORMAL slot on the opposite side (not big).
  {
    id: 'big-logo-l-weight-r',
    name: 'Big Badge',
    blurb: 'big logo left, weight right',
    family: 'Badge',
    label: 'Big logo left · weight right',
    elements: [
      { type: 'logo', slot: 'left', overrides: { big: true } },
      { type: 'weight', slot: 'right' },
    ],
  },
  {
    id: 'big-logo-r-weight-l',
    name: 'Big Badge Flip',
    blurb: 'big logo right, weight left',
    family: 'Badge',
    label: 'Big logo right · weight left',
    elements: [
      { type: 'logo', slot: 'right', overrides: { big: true } },
      { type: 'weight', slot: 'left' },
    ],
  },
  {
    // Big logo centered on the BOTTOM (top reaches the paintable ring's inner
    // edge, ≈½" past the metal; bottom flush with the ring) + weight top-center
    // (Ryan 2026-07-13).
    id: 'big-logo-b-weight-t',
    name: 'Big Badge Rise',
    blurb: 'big logo bottom, weight top',
    family: 'Badge',
    label: 'Big logo bottom · weight top',
    elements: [
      { type: 'logo', slot: 'bottom', overrides: { big: true } },
      { type: 'weight', slot: 'top' },
    ],
  },
  {
    // The vertical MIRROR of Big Badge Rise (Ryan 2026-07-25): big logo centered on the
    // TOP (top flush with the ring, bottom reaching the paintable ring's inner edge) +
    // weight bottom-center. bigLogoImageBox already handled 'top' symmetrically with
    // 'bottom', so this is the preset only — no geometry change.
    id: 'big-logo-t-weight-b',
    name: 'Big Badge Crown',
    blurb: 'big logo top, weight bottom',
    family: 'Badge',
    label: 'Big logo top · weight bottom',
    elements: [
      { type: 'logo', slot: 'top', overrides: { big: true } },
      { type: 'weight', slot: 'bottom' },
    ],
  },
  {
    /* ── Biggest Badge (Ryan 2026-08-10) — the discoverability one ─────────────────────────────
     * *"the logo dead center… big and smack dab in the middle of the plate. This will be good for
     * folks who are struggling to envision or understand that if they pick the big badge, they can
     * just nudge their logo to center, but until they click on this layout style, they won't know
     * it's possible, so it could go overlooked by lots of folks."*
     *
     * 🔑 THIS PRESET EXISTS TO BE SEEN, NOT TO BE POSSIBLE. The arrangement was always reachable by
     * nudging a Big Badge mark to the middle; what it never had was a thumbnail. A layout tile is
     * the only place this tool advertises what the nudge pad can do, so an arrangement nobody
     * stumbles into needs one even though the geometry underneath is not new.
     *
     * ⚠ THE WEIGHT SITS BOTTOM-CENTRE and therefore OVER the mark, which is unavoidable once the
     * mark owns the whole surface and is why it is nudgeable like every other Badge (Ryan: *"it will
     * behave like the other 4 badge layout types which allow you to nudge the weight number, nudge
     * the logo, enlarge the logo above 100%"*). Bottom rather than top to match Big Badge Crown,
     * whose arrangement — mark above, number below — a buyer arriving here has just been looking at.
     * One word to move it.
     *
     * ⚠ NEW ID, NEVER A REUSED ONE (§4 of the project CLAUDE.md): `getLayoutPreset` resolves an
     * unknown id to `LAYOUT_PRESETS[0]`, so a recycled id would answer "what family am I?" with the
     * wrong one for every design already saved under it. */
    id: 'big-logo-c-weight-b',
    name: 'Biggest Badge',
    blurb: 'big logo centred, weight bottom',
    family: 'Badge',
    label: 'Big logo centred · weight bottom',
    elements: [
      { type: 'logo', slot: 'center', overrides: { big: true } },
      { type: 'weight', slot: 'bottom' },
    ],
  },
  // ── Minimal family ─────────────────────────────────────────────────────────
  {
    id: 'logo-l-weight-r',
    name: 'Fieldhouse',
    blurb: 'logo left, weight right',
    family: 'Minimal',
    label: 'Logo left · weight right',
    elements: [
      { type: 'logo', slot: 'left' },
      { type: 'weight', slot: 'right' },
    ],
  },
  {
    id: 'weight-lr',
    name: 'Competition',
    blurb: 'weight both sides',
    family: 'Minimal',
    label: 'Weight both sides',
    elements: [
      { type: 'weight', slot: 'left' },
      { type: 'weight', slot: 'right' },
    ],
  },
  {
    id: 'weight-lr-right-inverted',
    name: 'Competition Mirror',
    blurb: 'weight both sides, mirrored',
    family: 'Minimal',
    label: 'Weight both sides · right inverted',
    elements: [
      { type: 'weight', slot: 'left' },
      { type: 'weight', slot: 'right', overrides: { upsideDown: true } },
    ],
  },
  {
    id: 'logo-tb-weight-lr',
    name: 'Four Corners',
    blurb: 'logo top & bottom, weight both sides',
    family: 'Minimal',
    label: 'Logo top & bottom · weight both sides',
    elements: [
      { type: 'logo', slot: 'top' },
      { type: 'logo', slot: 'bottom', overrides: { upsideDown: true } },
      { type: 'weight', slot: 'left' },
      { type: 'weight', slot: 'right', overrides: { upsideDown: true } },
    ],
  },
  // ── Dumbbell + FIXED BARBELL family ─────────────────────────────────────────
  // ONE set for both: `createBarbellGeometry` delegates to the dumbbell's head generator and only
  // stretches the handle, so a fixed barbell's heads ARE these heads.
  //
  // ── FIFTEEN, AS OF 2026-07-30 ────────────────────────────────────────────────
  // There were three, and they were the only thing in the app still designed against the wrong
  // surface: they resolved to the BUMPER's ring zone, so every piece was anchored on a circle at
  // 0.69R with a 68.8u box — on a face that is a solid disc with no hub, three times the room, and
  // nothing whatsoever in the middle. `capZone` is the face they actually have; these are Ryan's own
  // spec for it (2026-07-30), item by item:
  //
  //   1. medium+ logo just north of centre, small number centred below   → db-stacked
  //   2. the flip — number on top, logo below                            → db-stacked-flip
  //   3. logo left, number right                                         → db-side-by-side
  //   4. logo right, number left                                         → db-side-by-side-flip
  //   5. "versions similar to Fieldhouse, Competition, Competition       → db-four-corners
  //      Mirror and Four Corners"
  //      (Fieldhouse IS logo-left + weight-right, which is #3 — so it is that layout under this
  //       family's own name rather than a fourth entry painting the identical face. **Competition
  //       and Competition Mirror were built and then CUT** on Ryan's first look, along with the
  //       bare-number `db-numeral`: all three are number-only faces, and a dumbbell head with no
  //       mark on it is not what anyone commissions a custom set for. All three ids are BURNED, not
  //       recycled — see the note on `grip-tri-word` for what reusing one costs. Nothing outside
  //       this machine ever saw them: they were cut before the family's first deploy.)
  //   6. medium+ logo dead centre, numbers left and right                → db-medallion
  //   7. …and numbers above and below                                    → db-totem
  //   8. 6 and 7 again with arched text in the gaps between the numbers  → db-signet, db-sidelines
  //  10. large logo centre + ONE number where the shape allows, default   → db-badge
  //      bottom, logo nudged up to open the space under it
  //
  // …and item 9 ("any others the logic suggests"): **db-crest** — arch over a centred mark with the
  // number beneath, the arrangement every school badge in the reference pile uses, which this face
  // could not hold until it had a centre slot and a rim lane.
  //
  // ⚠ THE CENTRE SLOT IS NEW AND IS THE WHOLE POINT. `center` is the one place no ring product could
  // ever offer a piece: the bumper has a steel hub there and the grip plate a bore. Six of these
  // fifteen are built on it.
  //
  // ⚠ GROUPED INTO Arched / Minimal / Badge, NOT into one "Dumbbell" bucket. Three layouts under a
  // single heading was fine; fifteen is a wall. These are the same three buckets the bumper's
  // sixteen are already sorted into, so it is the picker's existing grouping doing its job, not a
  // new concept — and `LAYOUT_FAMILIES` needs no new entry.
  {
    id: 'db-full-face',
    name: 'Full Face',
    blurb: 'logo fills the whole face',
    family: 'Badge',
    product: 'dumbbell',
    label: 'Full-face logo',
    elements: [
      // bigFace:'disc' — a full-plate-diameter logo clipped to the outer circle
      // (render-engine's dumbbell-only path; a plain `big:true` would clip to the
      // bumper's ring annulus and leave a hole where the (nonexistent) hub is).
      //   The slot moved `top` → `center` with the cap zone. It never meant "at the top": this box
      // is centred on the face by construction and ignores its anchor entirely. Saying `top` was
      // harmless while every slot sat on one ring and is actively misleading now that `center`
      // exists and means it.
      { type: 'logo', slot: 'center', overrides: { big: true, bigFace: 'disc' } },
      // ⚠ AND A NUMBER, PAINTED OVER IT (Ryan 2026-07-30: *"the Full Face I'm looking at doesn't
      // have a number anywhere. Need it."*). It ships AFTER the logo in this list, which is what
      // puts it on top — elements paint in array order.
      //   This layout carried a standing note that a number here "would just overlap arbitrary logo
      // pixels", and that concern is real rather than resolved: a full-bleed mark leaves nowhere
      // clear to put one. What makes it shippable is that the app already answers exactly this
      // problem elsewhere — text over a dyed plate — with the outline stack and the contrast peel,
      // so the number arrives ringed and legible over most artwork rather than bare. The alternative
      // was shrinking the mark to open a clear band for it, and that is `db-badge`; a Full Face that
      // stopped reaching the edges would just be a second Big Badge.
      { type: 'weight', slot: 'bottom' },
    ],
  },
  {
    // Ryan #10: *"a large logo center, with 1 weight number in a place that makes the most sense
    // given the shape of the logo. Default should be center bottom if it fits, and then the logo
    // should be pushed up ever so slightly so it looks like the empty space is a tiny bit more at
    // the bottom to give that extra room for the number."*
    //
    // `bigBox` is NOT `big`: it keeps the mark on the ordinary point path — sized by the dial,
    // clipped to nothing, honestly centred — and only hands it a larger box. `big` would route it
    // through `bigLogoImageBox`, which is ring-B geometry and would punch a hole where this face
    // has no hub. The rise is `CAP_BADGE_RISE`, applied by the zone rather than by the preset, so a
    // buyer who nudges the mark by hand is moving it from where the layout put it.
    //
    // ⏳ "in a place that makes the most sense given the SHAPE of the logo" is half-built: bottom is
    // the default he asked for and is what ships. Choosing a SIDE for a tall mark means the slot
    // itself changing with the artwork, which is stored data the panel also reads — a different kind
    // of change, and one worth asking for rather than inferring.
    id: 'db-badge',
    name: 'Big Badge',
    blurb: 'large logo centred, weight below',
    family: 'Badge',
    product: 'dumbbell',
    label: 'Large centre logo · weight bottom',
    elements: [
      { type: 'logo', slot: 'center', overrides: { bigBox: true } },
      { type: 'weight', slot: 'bottom' },
    ],
  },
  {
    id: 'db-stacked',
    name: 'Stacked',
    blurb: 'logo above centre, weight below',
    family: 'Minimal',
    product: 'dumbbell',
    label: 'Logo above centre · weight below',
    elements: [
      { type: 'logo', slot: 'top' },
      { type: 'weight', slot: 'bottom' },
    ],
  },
  {
    id: 'db-stacked-flip',
    name: 'Stacked Flip',
    blurb: 'weight above, logo below centre',
    family: 'Minimal',
    product: 'dumbbell',
    label: 'Weight above · logo below centre',
    elements: [
      { type: 'weight', slot: 'top' },
      { type: 'logo', slot: 'bottom' },
    ],
  },
  {
    id: 'db-side-by-side',
    name: 'Side by Side',
    blurb: 'logo left, weight right',
    family: 'Minimal',
    product: 'dumbbell',
    label: 'Logo left · weight right',
    elements: [
      { type: 'logo', slot: 'left' },
      { type: 'weight', slot: 'right' },
    ],
  },
  {
    id: 'db-side-by-side-flip',
    name: 'Side by Side Flip',
    blurb: 'logo right, weight left',
    family: 'Minimal',
    product: 'dumbbell',
    label: 'Logo right · weight left',
    elements: [
      { type: 'logo', slot: 'right' },
      { type: 'weight', slot: 'left' },
    ],
  },
  {
    // ⚠ ROTATIONALLY SYMMETRIC: the bottom logo and the right number are both inverted, so the face
    // reads the same turned 180°. Ryan asked the question first — *"on Four Corners, should the
    // right-side weight number be upside down?"* — and he was right that something was off: this
    // preset had been copied from the bumper's and kept its flipped bottom LOGO while dropping its
    // flipped right NUMBER, which is neither of the two coherent answers.
    //   Offered upright-throughout on the reasoning that a cap is a face you look at head-on, where
    // an inverted number reads as a mistake rather than as the competition-plate convention it is on
    // a bumper. **He chose symmetric** (*"the logo on top has to be right-side up, while the logo on
    // bottom needs to be upside down… and the number on right needs to be upside down"*), which is
    // also what makes this layout the same design as the bumper's Four Corners rather than a
    // same-named cousin. Either way the rule is: all four pieces agree, or none do.
    id: 'db-four-corners',
    name: 'Four Corners',
    blurb: 'logo top & bottom, weight both sides',
    family: 'Minimal',
    product: 'dumbbell',
    label: 'Logo top & bottom · weight both sides',
    elements: [
      { type: 'logo', slot: 'top' },
      { type: 'logo', slot: 'bottom', overrides: { upsideDown: true } },
      { type: 'weight', slot: 'left' },
      { type: 'weight', slot: 'right', overrides: { upsideDown: true } },
    ],
  },
  {
    // Ryan #6. The centre slot's first real use: a medium+ mark dead centre with a number either
    // side of it — the arrangement a ring product physically cannot offer.
    id: 'db-medallion',
    name: 'Medallion',
    blurb: 'logo dead centre, weight either side',
    family: 'Minimal',
    product: 'dumbbell',
    label: 'Centre logo · weight left + right',
    elements: [
      { type: 'logo', slot: 'center' },
      { type: 'weight', slot: 'left' },
      { type: 'weight', slot: 'right' },
    ],
  },
  {
    // Ryan #7 — the same, turned through 90°.
    id: 'db-totem',
    name: 'Totem',
    blurb: 'logo dead centre, weight above and below',
    family: 'Minimal',
    product: 'dumbbell',
    label: 'Centre logo · weight top + bottom',
    elements: [
      { type: 'logo', slot: 'center' },
      { type: 'weight', slot: 'top' },
      { type: 'weight', slot: 'bottom' },
    ],
  },
  {
    // Item 9. Arch over a centred mark with the number beneath it — the school-badge arrangement,
    // impossible on this face until it had a centre slot and a rim lane.
    id: 'db-crest',
    name: 'Crest',
    blurb: 'arch top, logo centre, weight below',
    family: 'Arched',
    product: 'dumbbell',
    label: 'Arch top · centre logo · weight bottom',
    elements: [
      { type: 'arctext', slot: 'top', overrides: { text: 'YOUR TEXT' } },
      { type: 'logo', slot: 'center' },
      { type: 'weight', slot: 'bottom' },
    ],
  },
  {
    // Ryan #8, over Medallion: *"the gaps between the numbers include arched text (arch top and
    // bottom…)"*. The numbers hold 3 and 9 o'clock, so the words take 12 and 6.
    id: 'db-signet',
    name: 'Signet',
    blurb: 'arch top & bottom, logo centre, weight either side',
    family: 'Arched',
    product: 'dumbbell',
    label: 'Arch top & bottom · centre logo · weight left + right',
    elements: [
      { type: 'arctext', slot: 'top', overrides: { text: 'YOUR TEXT' } },
      // ⚠ NOT `upsideDown` (Ryan 2026-07-30: *"make the bottom arched text read rightside up, not
      // upside down"*). The bumper's own Seal hangs its lower arch inverted, which is the classic
      // seal convention on a plate read at any rotation — the same reasoning that put Four Corners
      // the other way round. A cap is read head-on, so the lower words stay upright and simply
      // follow the rim; `arcReadsInward` sends them round the reversed path, which is what keeps
      // them running left-to-right rather than mirrored.
      { type: 'arctext', slot: 'bottom', overrides: { text: 'GOES HERE' } },
      { type: 'logo', slot: 'center' },
      { type: 'weight', slot: 'left' },
      { type: 'weight', slot: 'right' },
    ],
  },
  {
    // Ryan #8, over Totem: *"…or arch left and right"*. The numbers hold 12 and 6, so the words take
    // the sides — and they FLOW around the circle rather than mirroring, so the pair reads as one
    // continuous ring of lettering. See CAP_ARC_SPANS.
    id: 'db-sidelines',
    name: 'Sidelines',
    blurb: 'words down both sides, logo centre, weight above and below',
    family: 'Arched',
    product: 'dumbbell',
    label: 'Arc left & right · centre logo · weight top + bottom',
    elements: [
      { type: 'arctext', slot: 'left', overrides: { text: 'YOUR TEXT' } },
      { type: 'arctext', slot: 'right', overrides: { text: 'GOES HERE' } },
      { type: 'logo', slot: 'center' },
      { type: 'weight', slot: 'top' },
      { type: 'weight', slot: 'bottom' },
    ],
  },
  // ── Grip family ─────────────────────────────────────────────────────────────
  // The grip plate's face is broken by three hand cutouts, so it can't take the
  // bumper's ring layouts at all: those run arc text through 0.505–0.873R and the
  // cutouts sit at 0.599–0.8515R. What's left are the three solid arcs BETWEEN the
  // cutouts, which render-engine's grip zone maps onto top / left / right (screen
  // 270 / 150 / 30). There is no `bottom` — screen 90 is the 6 o'clock hand hole.
  //
  // Two weight elements, not one: both numbers are real and independent (the
  // reference prints "45 -LB-" twice, once per lower gap). Nothing in the model
  // mirrors a piece across slots, and inventing that for this would be a lot of
  // machinery for a layout that reads fine as two pieces the buyer can edit.
  //
  // ── SIX, AS OF 2026-07-30 ────────────────────────────────────────────────────
  // `grip-classic` was the only one for two weeks, with a comment here saying not to invent
  // variants until Ryan had seen it rendered. He has, and specced these four designs:
  //   1. logo up top, weight numbers lower-left + lower-right      → grip-classic
  //   2. the flip — weight up top, logo in both lower gaps         → grip-classic-flip
  //   3. + 4. rim text in ALL THREE gaps, over (1) or (2) inboard of it → the Tri- pair below
  //
  // ── FOUR TRI- LAYOUTS BECAME TWO (Ryan 2026-07-30, third pass) ───────────────────────────────
  // There was briefly a second pair that repeated ONE line round all three gaps, driven by a single
  // Wording field. They are deleted: *"get rid of the Tri-Arch and Tri-Arch Flip… someone can choose
  // Tri-Word and Tri-Word Flip (rename these two Tri-Arch and Tri-Arch Flip), to create any
  // combination of different words around the 3 text areas, or the same around the 3 text-areas, and
  // having the words face up in all 3 looks best."*
  //   So this pair absorbed both jobs and took the better name. Repeating one line is now just what
  // happens when there aren't three pieces to split (`triWordsFor`), which is also why they need
  // **no `requires` at all**: *"any number of words (must be at least 1)… if someone chooses 1 or 2
  // words, it should default to repeating those 1 or 2 words in all 3 spots… if they choose 3+ words,
  // it should open up the possibility to having different words on top vs bottom left vs bottom
  // right."*
  //
  // WHAT MAKES THE WORDS FACE UP IS `upsideDown` ON THE LOWER TWO GAPS. The gaps sit at 120°, so an
  // unflipped rim arc in the lower pair is a ROTATED copy of the top arch and reads tilted — that was
  // the deleted pair's look. Flipping those two 180° about their own arc is what makes all three read
  // LEFT-TO-RIGHT across the plate (measured: at 150° an unflipped arc runs x=134→40; flipped, 40→134).
  //   That in turn REPLACED a per-glyph "upright" renderer which kept every letter vertical while its
  // position walked the rim — *"instead of twisting the letters to be perfectly upright… have it run
  // along the arch"*. `upsideDown` is the mechanism the bumper's bottom arch has always used, so this
  // is one well-worn path rather than two. The deleted renderer is in git at `74e56b0`.
  {
    id: 'grip-classic',
    name: 'Classic Grip',
    blurb: 'logo up top, weight either side below',
    family: 'Grip',
    product: 'grip',
    label: 'Logo top · weight lower-left + lower-right',
    elements: [
      { type: 'logo', slot: 'top' },
      { type: 'weight', slot: 'left' },
      { type: 'weight', slot: 'right' },
    ],
  },
  {
    id: 'grip-classic-flip',
    name: 'Grip Flip',
    blurb: 'weight up top, logo either side below',
    family: 'Grip',
    product: 'grip',
    label: 'Weight top · logo lower-left + lower-right',
    elements: [
      { type: 'weight', slot: 'top' },
      { type: 'logo', slot: 'left' },
      { type: 'logo', slot: 'right' },
    ],
  },
  {
    // ⚠ THE ID SAYS `tri-word` AND THE NAME SAYS "Tri-Arch". Not a mistake — a rename (Ryan
    // 2026-07-30: *"get rid of the Tri-Arch and Tri-Arch Flip… someone can choose Tri-Word and
    // Tri-Word Flip (rename these two Tri-Arch and Tri-Arch Flip), to create any combination of
    // different words around the 3 text areas, or the same around the 3 text-areas, and having the
    // words face up in all 3 looks best."*)
    //   The two layouts that HELD the name — one line repeated, driven by a single Wording field —
    // are deleted. They were redundant once these two could do the same thing by typing the same
    // word three times, and this pair reads better because all three words face up.
    //   IDS ARE NOT REUSED. `grip-tri-arch` was a real, different layout an hour ago, and a saved
    // design still carrying that id must not silently become this one on its next layout re-apply.
    // Renaming while keeping the id is the same call 160 made for the College family, for the same
    // reason: the id is what designs are pinned to, the name is what buyers read.
    id: 'grip-tri-word',
    name: 'Tri-Arch',
    blurb: 'a word in each gap, all facing up, logo up top',
    family: 'Arched',
    product: 'grip',
    // Cutout plates only. These are the grip family's TEXT layouts, and Ryan 2026-07-31 on
    // the 2.5/5: *"no text in any of the layouts."* They also arch a word along "the gap
    // between two hand cutouts", which on a hole-free face is not a place.
    weights: (w) => w > SMALL_GRIP_MAX_LB,
    label: 'Word ×3 · logo top · weight lower-left + lower-right',
    elements: [
      { type: 'arctext', slot: 'top', overrides: { text: 'YOUR TEXT' } },
      { type: 'arctext', slot: 'left', overrides: { text: 'GOES', upsideDown: true } },
      { type: 'arctext', slot: 'right', overrides: { text: 'HERE', upsideDown: true } },
      { type: 'logo', slot: 'top' },
      { type: 'weight', slot: 'left' },
      { type: 'weight', slot: 'right' },
    ],
  },
  {
    id: 'grip-tri-word-flip',
    name: 'Tri-Arch Flip',
    blurb: 'a word in each gap, all facing up, weight up top',
    family: 'Arched',
    product: 'grip',
    weights: (w) => w > SMALL_GRIP_MAX_LB, // cutout plates only — see grip-tri-word
    label: 'Word ×3 · weight top · logo lower-left + lower-right',
    elements: [
      { type: 'arctext', slot: 'top', overrides: { text: 'YOUR TEXT' } },
      { type: 'arctext', slot: 'left', overrides: { text: 'GOES', upsideDown: true } },
      { type: 'arctext', slot: 'right', overrides: { text: 'HERE', upsideDown: true } },
      { type: 'weight', slot: 'top' },
      { type: 'logo', slot: 'left' },
      { type: 'logo', slot: 'right' },
    ],
  },
  // ── The CUTOUT-FREE grip plates (2.5 / 5 lb) ────────────────────────────────────────────
  // Ryan 2026-07-31, naming what these two should offer: *"the layout options should be the
  // Classic Grip, Grip Flip, four corners, fieldhouse, competition, and competition mirror…
  // no text in any of the layouts."*
  //
  // Classic Grip and Grip Flip are the two above, shared unchanged — they use only
  // top/left/right, which the annular zone still has. The other four are the BUMPER's
  // Minimal arrangements, which fit because a hole-free grip face IS a plain annulus.
  //
  // ⛔ NEW IDS, NOT THE BUMPER'S. Copying `logo-l-weight-r` et al onto the grip family would
  // give one id two families, and `getLayoutPreset()` resolves an unknown/ambiguous id to
  // LAYOUT_PRESETS[0] — a bumper preset. A saved design would then answer "what family am I?"
  // with `plate`, and every family-based decision downstream goes quietly wrong (CLAUDE.md §4:
  // it once made `switchProduct` skip its rebuild and render a bumper's arrangement on a grip).
  //
  // The `weights` predicate keeps them off the cutout plates, where `bottom` is a hand hole.
  // Their FAMILY is 'Minimal' so the View-all grid groups them with the arrangements they
  // mirror, exactly as the bumper's do.
  {
    id: 'grip-sm-fieldhouse',
    name: 'Fieldhouse',
    blurb: 'logo left, weight right',
    family: 'Minimal',
    product: 'grip',
    weights: (w) => w <= SMALL_GRIP_MAX_LB,
    label: 'Logo left · weight right',
    elements: [
      { type: 'logo', slot: 'left' },
      { type: 'weight', slot: 'right' },
    ],
  },
  {
    id: 'grip-sm-competition',
    name: 'Competition',
    blurb: 'weight both sides',
    family: 'Minimal',
    product: 'grip',
    weights: (w) => w <= SMALL_GRIP_MAX_LB,
    label: 'Weight both sides',
    elements: [
      { type: 'weight', slot: 'left' },
      { type: 'weight', slot: 'right' },
    ],
  },
  {
    id: 'grip-sm-competition-mirror',
    name: 'Competition Mirror',
    blurb: 'weight both sides, mirrored',
    family: 'Minimal',
    product: 'grip',
    weights: (w) => w <= SMALL_GRIP_MAX_LB,
    label: 'Weight both sides · right inverted',
    elements: [
      { type: 'weight', slot: 'left' },
      { type: 'weight', slot: 'right', overrides: { upsideDown: true } },
    ],
  },
  {
    id: 'grip-sm-four-corners',
    name: 'Four Corners',
    blurb: 'logo top & bottom, weight both sides',
    family: 'Minimal',
    product: 'grip',
    weights: (w) => w <= SMALL_GRIP_MAX_LB,
    label: 'Logo top & bottom · weight both sides',
    elements: [
      { type: 'logo', slot: 'top' },
      { type: 'logo', slot: 'bottom', overrides: { upsideDown: true } },
      { type: 'weight', slot: 'left' },
      { type: 'weight', slot: 'right', overrides: { upsideDown: true } },
    ],
  },
]);

export const LAYOUT_PRESET_IDS = LAYOUT_PRESETS.map((p) => p.id);

/**
 * The preset a LAYOUT FAMILY opens on — each family's flagship.
 *
 * Lives here, next to the presets, because three callers need the same answer and
 * they must agree: main.js seeds the layout from the gate's pick and remembers a
 * per-family "last used" for product hopping, and state.js's smartStart re-seeds
 * on logo upload. Every MODELS.layoutFamily value must appear or those callers
 * strand the buyer on another family's preset — `test/ui-logic.smoke.mjs` checks it.
 */
export const FAMILY_DEFAULT_PRESET = Object.freeze({
  plate: DEFAULT_LAYOUT_PRESET,
  // ⚠ MOVED OFF `db-full-face` 2026-07-30. Ryan, specifying the cap family: *"the main go-to one for
  // most people will be a medium+ sized logo which sits just north of the center point, with a small
  // weight number centered below it"* — so that is what a dumbbell or fixed barbell opens on. Full
  // Face is a striking layout and a poor default: it carries no weight number at all, which on a
  // product a buyer is choosing denominations of is the one thing the first look should show.
  dumbbell: 'db-stacked',
  grip: 'grip-classic',
});

/** Looks up a preset by id (falls back to the first). */
export function getLayoutPreset(id) {
  return LAYOUT_PRESETS.find((p) => p.id === id) || LAYOUT_PRESETS[0];
}

const ARC_SLOT_KEYS = ['top', 'bottom', 'left', 'right'];

/**
 * THE WHOLE WORDING, ON ONE LINE — a memory key that is deliberately not a slot (2026-08-02).
 *
 * `savedArcText` remembers a typed line under the slot it was wearing, which is right while that slot
 * means "the top half of a pair". It is wrong for a layout that has only ONE arc, because there the
 * slot is an accident of that layout's geometry rather than a statement about the wording — and the
 * bumper has four top-only layouts and three bottom-only ones. Ryan's report (2026-08-02): design
 * DENVER / TIGERS, scroll through every layout, and the single-arc layouts carry the joined line
 * top → bottom → top, each hop remembering it under whichever slot it currently occupied. The map
 * ended up `{top:'Denver Tigers', bottom:'Denver Tigers'}` and the return to Varsity Arch restored
 * the same phrase onto both arches, permanently.
 *
 * `#` prefixed so it can never collide with a slot name a future product's zone invents (the
 * dumbbell's `center` is the next one). `serializeSavedArcText` does not whitelist keys — see its own
 * note for why — so this persists and shares with no schema change.
 */
const SOLO_ARC_KEY = '#solo';

/**
 * `savedArcText` in its per-FAMILY shape, migrating an older flat save on the way.
 *
 * It used to be `{ [slot]: text }` — one map shared by every product. That was fine while only the
 * bumper had arc text, and wrong the moment the grip plate arrived: `top` means the 12 o'clock arch on
 * one and the upper hand-gap on the other, so the two products silently overwrote each other's wording
 * (Ryan 2026-07-30). It is now `{ [family]: { [slot]: text } }`.
 *
 * A flat save is read as the PLATE's, because that is the only family that could have written one.
 */
function normaliseSavedArcText(saved) {
  if (!saved || typeof saved !== 'object') return {};
  const flat = ARC_SLOT_KEYS.some((k) => typeof saved[k] === 'string');
  if (!flat) return { ...saved };
  const legacy = {};
  for (const k of ARC_SLOT_KEYS) if (typeof saved[k] === 'string') legacy[k] = saved[k];
  const out = { plate: legacy };
  for (const k of Object.keys(saved)) if (!ARC_SLOT_KEYS.includes(k)) out[k] = saved[k];
  return out;
}

/** The wording a FAMILY remembers, or null. */
function savedArcTextFor(profile, family) {
  const store = normaliseSavedArcText(profile && profile.savedArcText);
  const mine = store[family];
  return mine && Object.keys(mine).length ? mine : null;
}

/**
 * A piece's identity for the per-family SIZE memory. Type AND slot, because a layout can hold two
 * of a type at different sizes — the top/bottom logo pair from Ryan's 2026-07-29 question is exactly
 * that, and keying on type alone would collapse them onto one number.
 *
 * ⚠ THE INK MEMORY DELIBERATELY DOES NOT USE THIS. It briefly did (2026-08-01) and that was the bug:
 * every always-black product shares one ink bucket while their element sets differ, so a per-piece key
 * applied a stored set to SOME of the destination's pieces and not others. Ink is stored per ROLE
 * instead — see `captureInk` in `ui/state.js`. Size is genuinely per-piece, so this key is right for
 * size and wrong for colour; the two questions only looked alike.
 */
export function elementKeyOf(el) {
  return `${el.type}:${el.slot || '-'}`;
}
/** Never had a legacy shape — `savedSizes` was born per-family — so this is a guard, not a migration. */
function normaliseSavedSizes(saved) {
  return saved && typeof saved === 'object' ? { ...saved } : {};
}
/** The hand-set sizes a FAMILY remembers, or null. */
function savedSizesFor(profile, family) {
  const mine = normaliseSavedSizes(profile && profile.savedSizes)[family];
  return mine && Object.keys(mine).length ? mine : null;
}
/**
 * The size this piece should wear in the family it is arriving in: its own remembered number, else
 * a same-TYPE sibling's, else null for "let the seeder write this family's default".
 *
 * The same-type fallback mirrors the single-slot fallback the wording restore uses below, and covers
 * the ordinary case of coming back to a family whose layout has since moved the piece to a different
 * slot — the buyer's "I like my numbers big" should survive that, rather than silently reverting.
 */
function rememberedSizeFor(store, el) {
  if (!store) return null;
  const exact = store[elementKeyOf(el)];
  if (Number.isFinite(exact)) return exact;
  const sibling = Object.keys(store).find((k) => k.startsWith(`${el.type}:`));
  return sibling ? store[sibling] : null;
}

/**
 * CAN THIS DESIGN ACTUALLY WEAR THIS LAYOUT? (Ryan 2026-07-30: *"if someone goes with an
 * organization that only has 2 words in the name, option 4 probably won't work… we'll need to gray
 * these layouts out and have a little message over them on all the layout cards"*.)
 *
 * The grip plate's Tri- layouts have three gaps and need three pieces of wording. A two-word name
 * cannot make them, and the honest failure is to say so on the card rather than paint a plate with
 * an empty gap — or, worse, the same word twice.
 *
 * ONE PREDICATE, BOTH PICKERS. The compact carousel and the "View all" grid ask this same function,
 * because a layout that is live in one and greyed in the other is worse than either answer.
 *
 * The test is "can `deriveArcTextTri` make three pieces", not a word count of its own — so it stays
 * correct as that function's school-name intelligence changes. "Denver High School" is three words
 * and passes; so is "Denver" + the mascot "Panthers" + … no, that is two, and it fails, correctly.
 *
 * A design that ALREADY has three worded arcs passes regardless — the buyer typed straight into the
 * Wording fields without ever filling in an organisation name, and telling them a layout they are
 * currently wearing is unavailable would be absurd.
 *
 * @param {LayoutPreset|string} preset
 * @param {object} profile
 * @returns {{ok:true}|{ok:false, reason:string, fix:'wording'}}
 */
export function presetEligibility(preset, profile) {
  const p = (typeof preset === 'string' ? getLayoutPreset(preset) : preset) || null;
  const need = p && p.requires && p.requires.words;
  if (!need) return ELIGIBLE;
  const els = (profile && profile.elements) || [];
  const worded = els.filter((e) => e && e.type === 'arctext'
    && String(e.text || '').trim() && !isPlaceholderText(e.text)).length;
  if (worded >= need) return ELIGIBLE;
  if (deriveArcTextTri({ name: profile && profile.orgName, mascot: profile && profile.mascot })) return ELIGIBLE;
  return {
    ok: false,
    // "organization", not "school" (Ryan 2026-07-30) — the field it points at is the Organization
    // field, and plenty of these plates go to gyms and clubs rather than schools.
    reason: 'Needs a three-word name — use your full organization name, or add a mascot.',
    short: 'Needs a three-word name',
    fix: 'wording',
  };
}
const ELIGIBLE = Object.freeze({ ok: true });

/**
 * Returns a fresh element array for a preset (pure — does not mutate anything).
 * @param {string} presetId
 * @returns {import('./design-profile.js').DesignElement[]}
 */
export function buildPresetElements(presetId) {
  const preset = getLayoutPreset(presetId);
  return preset.elements.map((e) => createElement(e.type, e.slot, e.overrides || {}));
}

/**
 * Carries the buyer's chosen STYLING (fill · outlines · size · weight-number format ·
 * letter-spacing) from an OLD element to a same-type NEW one — NOT its content (text /
 * logo art). Used both for a 1:1 migrate and to style an EXTRA new piece off a sibling.
 * Mutates + returns `newEl`.
 */
/**
 * The buyer's SIZE choice, carried across a layout change — for every element type, LOGOS INCLUDED.
 *
 * Its own function because it is the one piece of styling a logo shares with text, and because both
 * halves have to move together (Ryan 2026-07-29: *"If someone has a logo on the left and they
 * increase its size to 90%, then they change to a layout with the logo on top and bottom, should the
 * % stay at 90%? Right now it'll jump back down to something like 75%"*).
 *
 * ⚠ `sizeUserSet` TRAVELS WITH `sizeScale`. This is the same lesson `textUserSet` records below, in
 * the same file, for the same reason — and size is where it had not been learned yet. `setLayoutPreset`
 * deliberately re-seeds defaults after a migration, because the dial reads as a share of a room and a
 * layout change moves the room; that re-seed is guarded by `sizeUserSet`, so it "can only ever move a
 * piece nobody has sized by hand". Carry the number and drop the flag and the guard cannot fire: the
 * seeder sees a virgin piece, writes the 75% default over the buyer's 90%, and the carry is undone one
 * line later. Neither field is useful without the other. Never separate them.
 *
 * WHY 90% STAYS 90% AND NOT ITS PIXEL SIZE: `sizeScale` multiplies the room at the slot the piece is
 * standing in, and left/top/bottom rooms differ. So carrying the NUMBER carries the buyer's intent —
 * "nearly as large as it can be here" — and the mark paints a different absolute size in the new
 * arrangement. That is what a share-of-the-room dial means, and it is the same reasoning as
 * `wd-sizescale-is-a-share-not-a-number`.
 */
function migrateElementSize(oldEl, newEl) {
  if (!oldEl || !newEl || oldEl.type !== newEl.type) return newEl;
  if (Number.isFinite(oldEl.sizeScale)) newEl.sizeScale = oldEl.sizeScale;
  if (oldEl.sizeUserSet === true) newEl.sizeUserSet = true;
  return newEl;
}

function migrateElementStyle(oldEl, newEl) {
  if (!oldEl || !newEl || oldEl.type !== newEl.type || newEl.type === 'logo') return newEl;
  migrateElementSize(oldEl, newEl);
  if (oldEl.fillRef) newEl.fillRef = oldEl.fillRef;
  if (Array.isArray(oldEl.outlines)) newEl.outlines = oldEl.outlines.map((o) => ({ ...o }));
  if (newEl.type === 'weight' && oldEl.weightFormat) newEl.weightFormat = oldEl.weightFormat;
  /* ⛔ THE NUDGE IS DELIBERATELY *NOT* CARRIED HERE, and it was, for a few hours on 2026-08-09.
     Carrying it fixed the round trip (badge → arch → badge came back where the buyer left it) and
     broke something Ryan named immediately after: *"if I move the weight number on the full face
     layout for a dumbbell, the sidelines layout should have its weight numbers completely
     unaffected."* An element-level carry cannot tell those apart — it hands the offset to whatever
     layout comes next, which is right for the one you return to and wrong for the one you visit.
       Both are served by keying the MEMORY on product+layout and restoring it in `setLayoutPreset`
     (ui/state.js). A position is a property of the arrangement it was judged in, not cargo the
     element carries between arrangements. */
  if (newEl.type === 'arctext' && Number.isFinite(oldEl.letterSpacing)) newEl.letterSpacing = oldEl.letterSpacing;
  return newEl;
}

/**
 * Carries a customer's authored CONTENT + styling from an OLD element into the matching
 * NEW preset element of the same type (WD-01: relayout must not wipe the uploaded logo or
 * per-element edits). Mutates + returns `newEl`.
 */
function migrateElementContent(oldEl, newEl, carryText = true) {
  if (!oldEl || !newEl || oldEl.type !== newEl.type) return newEl;
  if (newEl.type === 'logo') {
    // Only carry real uploaded artwork — never an empty logo slot.
    if (oldEl.logo && oldEl.logo.dataUrl) newEl.logo = { ...oldEl.logo };
    // …and HOW THAT ARTWORK IS PAINTED travels with it (Ryan 2026-07-26). A tone the buyer recoloured
    // on the Add Logo tab is a property of their mark, not of the arrangement it sits in — the same
    // category as the image itself, which is why it belongs on this line and not with the styling
    // migration above. Without it, "Design My Weights" rebuilt the elements through smartStart and
    // the revealed plate wore the ORIGINAL logo colours while the text and numbers wore the new ones.
    //   The band overrides are deliberately NOT carried: they are derived from this list
    // (`applyToneSwapsToBands` in ui/state.js), so carrying the record is enough and there is only
    // ever one thing to keep in step.
    if (Array.isArray(oldEl.logoToneSwaps) && oldEl.logoToneSwaps.length) {
      newEl.logoToneSwaps = oldEl.logoToneSwaps.map((s) => ({ ...s }));
    }
    // AND THE REST OF THE LOGO'S PAINT (Ryan 2026-07-28: "go back to the competition bumper plate and
    // it reverts to a green plate with white text/logo as opposed to green text/logo with white
    // outlines"). This `return` sits before `migrateElementStyle`, and that function refuses logos
    // outright — so a logo's flatten colour and its contrast ring were carried by NOTHING and were
    // simply rebuilt from defaults on every layout change. A product hop changes layout family, so
    // "design it, look at the dumbbell, come back" lost the mark's colour every time.
    //   These are all buyer-facing choices, not derived values: the flatten they picked, the auto/
    // manual flag that tells the cascade whether it may re-decide it, the added ring, peeled bands,
    // and which bands they set by hand. `logoBandColors` comes too — it is no longer purely derived
    // from `logoToneSwaps` (a Logo fill pick writes it through `cascadeFillToBands`), so leaving it
    // behind drops half of a single-tone repaint.
    if (oldEl.logoCenterColor) newEl.logoCenterColor = oldEl.logoCenterColor;
    if (oldEl.logoCenterAuto === true) newEl.logoCenterAuto = true;
    if (oldEl.logoAddOutline) {
      newEl.logoAddOutline = Array.isArray(oldEl.logoAddOutline)
        ? oldEl.logoAddOutline.map((a) => ({ ...a }))
        : { ...oldEl.logoAddOutline };
    }
    if (Array.isArray(oldEl.logoBandsOff) && oldEl.logoBandsOff.length) newEl.logoBandsOff = [...oldEl.logoBandsOff];
    if (Array.isArray(oldEl.logoBandUserSet) && oldEl.logoBandUserSet.length) newEl.logoBandUserSet = [...oldEl.logoBandUserSet];
    if (oldEl.logoBandColors && typeof oldEl.logoBandColors === 'object') newEl.logoBandColors = { ...oldEl.logoBandColors };
    /* ⛔ THE BADGE NUDGE IS NOT CARRIED HERE EITHER — see the note in `migrateElementStyle`. It was,
       briefly, and for the same good reason; the layout-keyed memory in `setLayoutPreset` replaced
       it because Big Badge and Big Badge Flip are two arrangements and a mark placed by eye in one
       has no business landing in the other. */
    /* …AND THE SIZE, which this branch's `return` used to skip past — see migrateElementSize.
       The comment above already noted that this early return plus migrateElementStyle's refusal of
       logos left a logo's colour carried by NOTHING; the SIZE was the last member of that family.

       ⛔ EXCEPT ACROSS THE BIG/SLOT BOUNDARY (Ryan 2026-08-09). A slot logo's scale is a share of its
       slot and means roughly the same apparent size on every arch layout, which is why those stay in
       lock step. A BIG mark is placed by a rule against the ring or the face and is several times
       larger, so the same number is a different animal — carrying 20% off a Big Badge made every
       arch layout tiny, and carrying an arch's number onto a badge did the reverse.
         Two scales that do not mean the same thing must not be handed to one another. Within each
       kind the carry is untouched; a big mark's own size travels by the per-layout record instead
       (`rememberWeightNudge` in ui/state.js), which is what gives Badge and Badge Flip separate
       answers. */
    if ((oldEl.big === true) === (newEl.big === true)) migrateElementSize(oldEl, newEl);
    return newEl;
  }
  migrateElementStyle(oldEl, newEl); // fill · outlines · size · weightFormat · letter-spacing
  // Carry the buyer's real text; if the old text was still a placeholder, keep the new
  // preset's placeholder rather than dragging "YOUR TEXT" around.
  //
  // ⚠ `textUserSet` TRAVELS WITH THE WORDS. Carrying the text but not the flag was the root of a
  // whole family of arc-wording bugs (Ryan 2026-07-28, twice): downstream, a migrated line was
  // indistinguishable from one the app had derived, so `reseedIdentityIntoPlaceholders` had to GUESS
  // who wrote it — is it a placeholder, does it match a derived half, is its sibling empty — and
  // every guess was a way to be wrong. The flag answers outright, and it is only meaningful if it
  // survives the trip. Never separate them again.
  // ⛔ NOT ACROSS FAMILIES (Ryan 2026-07-30: *"when the wording is changed on this model, it will
  // affect the wording on bumper and grip plate models too — when it shouldn't"*).
  //
  // A 1:1 carry is obviously right between two layouts of ONE product: the buyer is rearranging
  // their own plate and the words are theirs. Between two PRODUCTS it is the leak itself — it copies
  // the source's line into the destination, stamped `textUserSet`, before either of the rules that
  // are supposed to answer this question gets a look in. The destination's own remembered wording
  // and the identity-derived split were both being written over by a migration that ran first and
  // knew about neither.
  //   This is the third layer of the same bug and the one underneath the other two: `savedArcText`
  // (2026-07-30, keyed by family) protects a line a product was GIVEN, and the combine rule now
  // stands down when an identity exists — but neither can help while the words arrive by a route
  // that predates both. Style still travels (see `migrateElementStyle` above): a fill and an outline
  // stack are choices about the design, not about this product's wording.
  if (carryText && newEl.type === 'arctext' && oldEl.text && !isPlaceholderText(oldEl.text)) {
    newEl.text = oldEl.text;
    if (oldEl.textUserSet === true) newEl.textUserSet = true;
  }
  return newEl;
}

/**
 * Applies a preset to a profile IN PLACE: sets layoutPreset + rebuilds elements.
 * By default it PRESERVES the buyer's content (uploaded logo, real arc text,
 * fills/outlines/sizes) by migrating same-type elements into the new slots
 * (WD-01). Pass `{ preserve:false }` for a hard reset (e.g. the initial seed).
 * @param {import('./design-profile.js').DesignProfile} profile
 * @param {string} presetId
 * @param {{preserve?:boolean}} [opts]
 * @returns {import('./design-profile.js').DesignProfile}
 */
export function applyLayoutPreset(profile, presetId, { preserve = true } = {}) {
  const preset = getLayoutPreset(presetId);
  // Captured before anything mutates it — `profile.layoutPreset` is only overwritten at the very end,
  // but reading it from two places invites the day someone moves that line.
  const prevPresetId = profile.layoutPreset;
  const srcFamily = layoutPresetProduct(getLayoutPreset(prevPresetId));
  const destFamily = layoutPresetProduct(preset);
  const crossFamily = srcFamily !== destFamily;
  const prev = preserve && Array.isArray(profile.elements) ? profile.elements.slice() : [];
  const next = buildPresetElements(preset.id);
  // The sizes this preset seeds ITSELF with, captured before the migration below overwrites them.
  // On a cross-family hop a piece with nothing remembered has to go back to exactly this, so the
  // re-seed in `setLayoutPreset` treats it as untouched — see the restore block near the end.
  const pristineSize = new Map(next.map((ne) => [ne, ne.sizeScale]));
  if (prev.length) {
    const pools = { logo: [], weight: [], arctext: [] };
    prev.forEach((e) => { if (pools[e.type]) pools[e.type].push(e); });
    const cursor = { logo: 0, weight: 0, arctext: 0 };
    next.forEach((ne) => {
      const pool = pools[ne.type];
      if (pool && cursor[ne.type] < pool.length) migrateElementContent(pool[cursor[ne.type]++], ne, !crossFamily);
      // A NEW piece a layout switch introduces with no 1:1 predecessor (e.g. a 2nd weight
      // when the old layout had one): match an EXISTING same-type sibling's STYLE (fill /
      // outlines / weight-number format), so the 2nd "45 POUNDS" keeps the outlines +
      // format the buyer chose instead of resetting to -LB- + one outline (Ryan 2026-07-22).
      // `migrateElementStyle` refuses logos, so the SIZE is passed separately — this is the exact
      // case in Ryan's question (2026-07-29): logo on the LEFT at 90% → a layout with logos top AND
      // bottom. The top one has a 1:1 predecessor and is handled above; the bottom one is a brand
      // new piece, and without this it seeds at the 75% default while its twin wears 90%. Two logos
      // in one design at different sizes is worse than either number being wrong.
      /* ⛔ AND THE SAME BIG/SLOT GUARD HERE (Ryan 2026-08-09: *"put logo size on BIG BADGE down to
         20%, then the SVG for FOUR CORNERS showed one of the logos at the regular correct size, but
         another one was tiny at 20% too"*).
           That sentence names this branch precisely. Four Corners has more logos than a Badge has, so
         the FIRST one matches 1:1 and goes through `migrateElementContent`, which learned the guard —
         correct size. Every logo after it has no predecessor and lands here, where the size was still
         being copied off the big mark unguarded. Hence one right and one tiny, in the same card.
           Two places answering "may this size travel?", one of them updated: CLAUDE.md §0b. */
      else if (pool && pool.length) {
        const src = pool[pool.length - 1];
        migrateElementStyle(src, ne);
        if ((src.big === true) === (ne.big === true)) migrateElementSize(src, ne);
      }
      // Truly no sibling of this type → stamp the logo-matched auto-style so it doesn't fall
      // back to the plain default outline (B1, Ryan 2026-07-17). No-op when there's no autoStyle.
      else if ((ne.type === 'weight' || ne.type === 'arctext') && profile.savedTextStyle) {
        // No same-type predecessor — but the design HAD a style before it passed through a layout
        // without these slots. Prefer the buyer's over the auto-style; see savedTextStyle above.
        const st = profile.savedTextStyle;
        if (st.fillRef) ne.fillRef = st.fillRef;
        if (st.fillUserSet) ne.fillUserSet = true;
        if (st.outlines.length) ne.outlines = st.outlines.map((o) => ({ ...o }));
      } else applyAutoStyleToElement(profile, ne);
    });
    // Remember the uploaded logo on the PROFILE before rebuilding elements, so it
    // survives switching THROUGH a layout that has no logo slot (a text-only
    // layout). Otherwise the image had nowhere to live and was dropped — every
    // later logo layout (and any you went back to) came up blank, unrecoverable
    // without re-uploading (Ryan 2026-07-13).
    const srcLogo = prev.find((e) => e.type === 'logo' && e.logo && e.logo.dataUrl);
    if (srcLogo) {
      profile.savedLogo = { ...srcLogo.logo };
      // The mark's PAINT is remembered with it, for the identical reason: a trip through a text-only
      // layout leaves the recolour with nowhere to live, and the logo would come back in its original
      // colours the next time a logo slot appeared.
      profile.savedLogoToneSwaps = Array.isArray(srcLogo.logoToneSwaps) && srcLogo.logoToneSwaps.length
        ? srcLogo.logoToneSwaps.map((s) => ({ ...s }))
        : null;
    }
    // Same treatment for arch wording the buyer TYPED (Ryan 2026-07-25). Migration only carries text
    // between layouts that both have arc slots, so a trip through a Badge — which has none — dropped
    // it. orgName/mascot cover the derived case, but a line typed straight into Customize Design →
    // Wording with no organisation name had nowhere to live and came back as YOUR TEXT.
    //   ONLY `textUserSet` lines are remembered, and that restriction is the point: a DERIVED line
    // must stay derived, or editing the org name while on a text-less layout would be overwritten by
    // the stale split when you switch back.
    //   ⚠ REMEMBERED PER **FAMILY**, NOT PER SLOT ALONE (Ryan 2026-07-30). The bumper's `top` and the
    // grip's `top` are the same string key and completely different places on completely different
    // products, so one flat map made them overwrite each other. He typed ARCADIA / ANCHOVIES on a
    // bumper, hopped to the grip, and its top gap inherited "Arcadia" — where he expected the two
    // lines COMBINED — and the grip's view then leaked back over what he had left on the bumper
    // ("I was expecting to see what I had left behind still intact… wtf").
    //   Keyed by family, each product remembers only its own wording, and neither can tread on the
    // other. See `savedArcTextFor` for how an older flat save is read.
    //   ⚠ …AND A ONE-ARC LAYOUT REMEMBERS ITS LINE AS **THE WHOLE WORDING**, NOT AS ITS SLOT
    // (Ryan 2026-08-02) — see `SOLO_ARC_KEY` for the trace. The two are different facts and storing
    // them under the same kind of key let one overwrite the other: `top` off a Marquee is the entire
    // design's wording that happens to be arched at 12 o'clock, while `top` off a Varsity Arch is one
    // half of a pair. Written under its own key, a tour through every single-arc layout can no longer
    // reach the pair the buyer left on the two-arc one.
    const prevArcs = prev.filter((e) => e.type === 'arctext');
    const wholeLineSource = prevArcs.length === 1;
    for (const e of prevArcs) {
      if (e.textUserSet !== true) continue;
      if (!e.text || isPlaceholderText(e.text)) continue;
      const store = normaliseSavedArcText(profile.savedArcText);
      const mine = { ...(store[srcFamily] || {}) };
      if (wholeLineSource) {
        mine[SOLO_ARC_KEY] = e.text;
        // The per-slot pair is a DECOMPOSITION of the whole line, so it stays valid exactly as long
        // as its pieces are still IN that line. Edit the joined line on a Marquee — DENVER TIGERS →
        // BOULDER BEARS — and the remembered DENVER / TIGERS is stale; keeping it would beat the
        // fresher line on the way back (the restore prefers an exact slot match, and rightly so).
        //   Dropped as a SET, never piecemeal: half a decomposition is not a design, and a partial
        // edit would otherwise restore one real word beside one blank arc.
        const whole = e.text.toUpperCase();
        if (ARC_SLOT_KEYS.some((s) => mine[s] && !whole.includes(String(mine[s]).toUpperCase()))) {
          for (const s of ARC_SLOT_KEYS) delete mine[s];
        }
      } else {
        mine[e.slot] = e.text;
        delete mine[SOLO_ARC_KEY]; // the pair is the newer statement — the mirror of the prune above
      }
      store[srcFamily] = mine;
      profile.savedArcText = store;
    }
    // ⛔ AND NEITHER DOES SIZE — the FOURTH layer of the lesson above (Ryan 2026-07-30: *"sizes is a
    // bit subjective… some people will like the look of a larger number on the small-surfaced
    // dumbbell with a smaller number on the large-surfaced bumper, so forcing them into a flat 'x%'
    // across both might not suit their style"*).
    //
    // Font, colour and the number style stay COUPLED across models on purpose — those are one
    // design identity worn by a whole set. Size is the odd one out because the dial reads as a share
    // of a room and the rooms are different shapes: 75% of a bumper's ring is not 75% of a dumbbell's
    // disc in any sense the eye agrees with.
    //
    // Only `sizeUserSet` pieces are remembered, and that restriction is doing the same job it does
    // for the wording: an untouched piece must stay untouched so `setLayoutPreset`'s re-seed can give
    // it the DESTINATION family's default. That is what keeps Ryan's other requirement true — the
    // shipped defaults remain the one-size-fits-all approximation, and only a deliberate edit sticks.
    if (crossFamily) {
      const store = normaliseSavedSizes(profile.savedSizes);
      const mine = { ...(store[srcFamily] || {}) };
      for (const e of prev) {
        if (e.sizeUserSet !== true || !Number.isFinite(e.sizeScale)) continue;
        mine[elementKeyOf(e)] = e.sizeScale;
      }
      if (Object.keys(mine).length) { store[srcFamily] = mine; profile.savedSizes = store; }
    }
    // The lettering's STYLE gets the same treatment as its words, and for the same reason (Ryan
    // 2026-07-28). Migration can only carry style between layouts that BOTH have text slots; the
    // dumbbell family has none, so a hop through it destroyed the text elements and the hop back
    // found no donor and fell through to `applyAutoStyleToElement` — green text with white outlines
    // came back as the plain default. `savedArcText` already solved exactly this for the words.
    //   Remembered from the last element that actually carries a choice, so a design nobody has
    // styled leaves this null and the auto-style still applies.
    const styleDonor = prev.find((e) => (e.type === 'weight' || e.type === 'arctext')
      && (e.fillUserSet === true || (Array.isArray(e.outlines) && e.outlines.length)));
    if (styleDonor) {
      profile.savedTextStyle = {
        fillRef: styleDonor.fillRef || null,
        fillUserSet: styleDonor.fillUserSet === true,
        outlines: Array.isArray(styleDonor.outlines) ? styleDonor.outlines.map((o) => ({ ...o })) : [],
      };
    }
  }
  // Restore those typed lines into any matching slot the new layout seeded with a placeholder. Runs
  // BEFORE reseedIdentityIntoPlaceholders, which then only fills what's genuinely still empty — so a
  // buyer's own wording outranks the name-derived split, exactly as it does on the plate.
  const savedText = savedArcTextFor(profile, destFamily);
  // Which pieces the memory actually spoke for. Read by the cross-family combine rule below, which
  // used to re-derive the same question from `ownMemory[ne.slot]` — a test that could not see a line
  // restored under `SOLO_ARC_KEY` and would have overwritten it. One place decides, one place asks.
  const restoredFromMemory = new Set();
  if (savedText) {
    const arcSlots = next.filter((ne) => ne.type === 'arctext');
    // The whole wording on one line, if that is what this family last showed. On a layout with more
    // arcs than that line came off, it is RE-CUT rather than repeated — the same "join is the inverse
    // of split" invariant the redistribution rules below already hold themselves to, and the reason
    // arriving at a two-arc layout from a text-less one no longer prints the joined phrase twice.
    const soloLine = savedText[SOLO_ARC_KEY] || null;
    const hasSlotMemory = arcSlots.some((ne) => savedText[ne.slot]);
    const spread = (!hasSlotMemory && soloLine && arcSlots.length > 1)
      ? distributeAcrossArcs(soloLine, arcSlots.length)
      : null;
    // A layout with ONE arc slot takes whichever line we remember, even from the other slot —
    // otherwise typing a top line and switching to a bottom-only preset shows YOUR TEXT while the
    // buyer's words sit in the Wording field. Mirrors reseedIdentityIntoPlaceholders' single-slot
    // fallback. With two slots, only an exact slot match applies: filling the second arc from the
    // first would repeat the wording on both without being asked.
    const anyRemembered = ARC_SLOT_KEYS.map((s) => savedText[s]).find(Boolean);
    arcSlots.forEach((ne, i) => {
      const remembered = savedText[ne.slot]
        || (arcSlots.length === 1 ? (soloLine || anyRemembered) : (spread ? spread[i] : null));
      // ⚠ ON A CROSS-PRODUCT HOP THE MEMORY WINS OUTRIGHT, placeholder or not. The 1:1 migration has
      // already copied the OTHER product's wording into these slots, so the usual "only fill what is
      // still empty" test declines and the buyer's own line never comes back — which is exactly what
      // Ryan hit returning to the bumper: "I was expecting to see what I had left behind still
      // intact… wtf". Within one family the old rule stands, because there the migration is carrying
      // the design's own words between two of its own layouts.
      const mayWrite = crossFamily || !ne.text || isPlaceholderText(ne.text);
      if (remembered && mayWrite) { ne.text = remembered; ne.textUserSet = true; restoredFromMemory.add(ne); }
    });
  }
  // ── ONE LINE OF WORDING, TWO ARCS TO FILL → SPLIT IT (Ryan 2026-07-29) ─────────────────────────
  // He typed "Gym which is awesome" into Marquee's single arch, picked Varsity Arch, and got the whole
  // phrase on top with **"is awesome"** underneath. Nobody designed that: the two arcs were filled by
  // two passes that don't know about each other. The 1:1 MIGRATION above copies the old top arc's text
  // verbatim into the new top arc, and `reseedIdentityIntoPlaceholders` (which runs straight after
  // this function, from setLayoutPreset) then fills the still-placeholder bottom from `deriveArcText`
  // — which SPLITS the same words into {top:'Gym which', bottom:'is awesome'} and has its top half
  // thrown away. So the phrase's tail printed twice and its head not at all.
  //
  // The fix is ONE source for both arcs, and the source is the SPLIT, not duplication:
  //   · `reseedIdentityIntoPlaceholders` already carries a guard against printing the same name on
  //     both arcs, so this codebase already treats duplication as the wrong answer.
  //   · Its own comment defines a single arc as "the WHOLE identity on one line — the SAME WORDS the
  //     two-arc layout shows, JOINED." Splitting is the exact inverse of that join, which is what
  //     makes Marquee ↔ Varsity Arch round-trip to the same words instead of drifting each hop.
  //   · `balanceSplit` picks the word boundary that most evenly divides the line, so real inputs land
  //     where a designer would put them: "Battery Creek High School" → BATTERY CREEK / HIGH SCHOOL.
  //
  // ⚠ POSITION IS THE WHOLE FIX. This has to run AFTER the migration and the savedArcText restore —
  // it is overriding what they wrote, not filling a gap they left. A first attempt lived inside the
  // savedArcText block and did nothing, because by then the migration had already put the full phrase
  // in the top slot and the "still a placeholder?" guard correctly declined to touch it.
  //
  // Keyed on the OLD design (`prev`), not on what landed: exactly one arc carrying real words, going
  // into a layout with MORE arc slots than that. A buyer who typed every arc is left alone. A short
  // or one-word line has nothing to split, and `deriveArcText` twin-arches it ("Gym" → GYM / GYM) —
  // its existing behaviour, deliberately unchanged.
  //
  // ⚠ THE TWO-ARC CASE STILL GOES THROUGH `deriveArcText`, NOT `balanceSplitN(…, 2)`. They agree on
  // the boundary, but not on everything: `deriveArcText` also twin-arches an unsplittable line,
  // which is the behaviour above and which the general splitter deliberately refuses (three gaps
  // showing one word three times is not a design). Routing the pair case through the n-way splitter
  // would have quietly deleted that fallback.
  if (prev.length) {
    const prevWorded = prev.filter((e) => e.type === 'arctext'
      && String(e.text || '').trim() && !isPlaceholderText(e.text));
    const nextArcs = next.filter((ne) => ne.type === 'arctext');
    const arcAt = (slot) => nextArcs.find((ne) => ne.slot === slot);
    // ⚠ `typed` IS THE PROVENANCE OF THE SOURCE WORDS, NOT A CONVENIENCE (Ryan 2026-08-02).
    // Redistribution moves words around; it does not get to change WHO WROTE THEM. Stamping
    // `textUserSet` on a line the app derived is the bug CLAUDE.md §5 names — it makes derived text
    // indistinguishable from typed text, and `reseedIdentityIntoPlaceholders` reads that flag as its
    // one exception, so a frozen derivation stops tracking the identity for the rest of the session.
    //   The flag is written either way rather than merely set: the 1:1 migration may already have
    // carried a `true` in from a source that had itself been mis-stamped, and leaving it would let
    // one bad hop poison every hop after it.
    const setArc = (ne, text, typed = true) => {
      if (!ne || !text) return;
      ne.text = text;
      ne.textUserSet = typed === true;
    };
    // ⚠ THE THREE-GAP RULES BELOW REDISTRIBUTE **TYPED** WORDS ONLY, and that restriction is the
    // whole of their correctness. Redistribution stamps `textUserSet`, which is a promise that a
    // human chose these words — it is what stops `reseedIdentityIntoPlaceholders` re-deriving over
    // them one line later. Stamping it on lines the APP derived freezes a derivation, and a frozen
    // derivation is strictly worse than a fresh one, because re-deriving from the organisation name
    // can see words the arcs no longer carry.
    //   Caught by "Denver North City High School": the bumper's two-arc derivation drops the school
    // suffix as lopsided and shows DENVER / NORTH CITY, so hopping to a three-gap layout had only
    // those four words to work with and cut them into DENVER / NORTH / CITY. Left alone, the reseed
    // asks `deriveArcTextTri` for the FULL name and gets DENVER / NORTH CITY / HIGH SCHOOL — the
    // right answer, which no amount of re-splitting the plate could have recovered.
    //   The two pre-existing pair rules are deliberately NOT gated this way. They are covered by
    // `tools/verify-arc-split-roundtrip.mjs`, their behaviour is what those cases assert, and this
    // change has no business rewriting them.
    const prevTyped = prevWorded.filter((e) => e.textUserSet === true);
    // ── ONE LINE TYPED INTO SEVERAL SLOTS MEANS "THIS LINE, EVERYWHERE" (Ryan 2026-07-30) ─────────
    // *"Should the bumper consist of the same words on top and bottom, then when you go to the grip
    // plate, it should consist of those same words in all 3 spots."*
    //
    // ⚠ THIS IS THE FIX FOR A HOP CYCLE THAT NEVER SETTLED. Typing "Arcadia Anchovies" into all three
    // grip gaps and then hopping back and forth produced, in order: ARCADIA / ANCHOVIES on the bumper
    // → ARCADIA / ANCHOVIES / ARCADIA ANCHOVIES on the grip → ARCADIA ANCHOVIES twice on the bumper →
    // all three again on the grip → and round for ever. The identity was never wrong (`orgName` read
    // "Arcadia Anchovies" throughout); the ARCS were drifting on their own, because each hop
    // redistributed the PREVIOUS hop's derivation and stamped the result `textUserSet`, which made
    // derived text indistinguishable from typed text and stopped it ever being re-derived.
    //
    // Repeating is the FIXED POINT of that loop: three-of-the-same → two-of-the-same → three-of-the-
    // same, and the sequence stops moving. It is also what the buyer plainly asked for by typing the
    // same words three times.
    //
    // ⚠ AND IT MUST NOT SWALLOW THE 1 → 2 SPLIT below it, which Ryan asked for on 2026-07-29 ("Gym
    // which is awesome" typed into Marquee's single arch becomes GYM WHICH / IS AWESOME on a Varsity
    // Arch). The distinguishing signal is **how many slots they typed it into**: one slot is a line
    // that happens to be alone and may be split; two or more slots carrying the SAME string is a
    // deliberate statement about every slot. Hence `>= 2`.
    const distinctTyped = [...new Set(prevTyped.map((e) => String(e.text).trim()))];
    // ── CROSSING TO ANOTHER PRODUCT: ONE COMBINED LINE, IN EVERY SLOT (Ryan 2026-07-30) ───────────
    // *"Should the bumper consist of the same words on top and bottom, then when you go to the grip
    // plate, it should consist of those same words in all 3 spots. If the bumper has 1 set of words on
    // top and 1 set on bottom, then the grip plate should combine them by default into 1 string and
    // repeat that 1 string in all 3 places. This should be the default rule that governs the entire
    // session."*
    //
    // Both of his clauses are ONE rule once you notice that joining a set of identical lines gives
    // back the line itself: **reconstruct the whole wording, then put it in every slot.** It is also
    // the FIXED POINT of a hop — whatever the destination has, joining it back gives the same string —
    // which is what stopped the wording cycling for ever between the two products.
    //
    // ⚠ CROSS-FAMILY ONLY, and that restriction is what keeps every same-product rule intact. Inside
    // one family the existing split/join below still governs: a line typed into Marquee's single arch
    // still SPLITS across a Varsity Arch (2026-07-29), because there the buyer never left the bumper
    // and the two arcs are two halves of one design rather than two products' views of one identity.
    //
    // ⚠ AND ONLY WHERE THE DESTINATION HAS NO MEMORY OF ITS OWN. The restore above has already put
    // back anything this family was carrying, and those are the buyer's per-product overrides — the
    // "locks onto that deviation" half of his rule. Going grip → bumper must show the ARCADIA /
    // ANCHOVIES he left on the bumper, not the grip's combined view of it.
    // ⚠ …AND ONLY WHEN THERE IS NO IDENTITY TO DERIVE FROM (Ryan 2026-07-30: *"when the wording is
    // changed on this model, it will affect the wording on bumper and grip plate models too — when it
    // shouldn't"*).
    //   This rule and that complaint are the same mechanism seen from two sides. Carrying the wording
    // across is right for a design that has nothing else to say — a buyer who typed straight into the
    // Wording boxes and never named an organisation would otherwise meet YOUR TEXT on the next
    // product. It is wrong the moment an identity exists, because then the destination has its OWN
    // correct answer: `reseedIdentityIntoPlaceholders` cuts the name to that product's own slots, per
    // product, stably. Writing the source product's line over it first — stamped `textUserSet`, which
    // stops the reseed touching it — is what made an edit on the dumbbell rewrite the bumper.
    //   So: own memory first, then the identity, and this only when there is neither. The three are
    // in preference order and each is strictly more specific than the one after it.
    const hasIdentity = String((profile && profile.orgName) || '').trim() !== ''
      || String((profile && profile.mascot) || '').trim() !== '';
    if (crossFamily && distinctTyped.length && !hasIdentity) {
      const whole = distinctTyped.join(' ');
      // ⚠ SKIP BY **MEMORY**, NOT BY `textUserSet`. The 1:1 migration has already run and it carries
      // that flag across with the text, so every slot it happened to fill looked like a deliberate
      // override and the combined line was written nowhere. What actually deserves protection is a
      // line THIS family remembers being given — which is the restore just above, and is exactly the
      // set of slots `ownMemory` names.
      //   ⚠ ASKED OF THE RESTORE, not re-derived from the store (2026-08-02). This read
      // `ownMemory[ne.slot]`, which is the restore's condition copied out by hand — and the copy went
      // stale the moment a remembered line could arrive under `SOLO_ARC_KEY` instead of a slot, at
      // which point this would have written the combined line straight over it. The restore records
      // what it filled; this asks. (CLAUDE.md §0b — when two places need the same answer, one of them
      // will be wrong.)
      nextArcs.forEach((ne) => {
        if (restoredFromMemory.has(ne)) return;
        setArc(ne, whole);
      });
    }
    // ⚠ A FLAG, not a re-test of the condition. The rules below were first gated on
    // `distinctTyped.length !== 1`, which is ALSO true when only ONE arc was typed — so a single
    // joined line coming back from a Marquee stopped being re-split across the grip's three gaps and
    // landed whole in the first one. What they actually need to know is whether the repeat rule has
    // already spoken for every slot.
    const repeated = !crossFamily && prevTyped.length >= 2 && distinctTyped.length === 1 && nextArcs.length > 0;
    if (repeated) {
      nextArcs.forEach((ne) => setArc(ne, distinctTyped[0]));
    } else if (!crossFamily && prevWorded.length === 1 && nextArcs.length === 2) {
      const top = arcAt('top');
      const bottom = arcAt('bottom');
      if (top && bottom) {
        const pair = deriveArcText({ name: String(prevWorded[0].text).trim() });
        if (pair.top && pair.bottom) {
          // `textUserSet` follows THE SOURCE, matching how the savedArcText restore treats a carried
          // line: redistributed buyer's words are still the buyer's, and the identity reseed must not
          // overwrite them — but redistributed DERIVED words are still derived, and the reseed is
          // entitled to replace them with this layout's own split of the identity. See `setArc`.
          const typed = prevWorded[0].textUserSet === true;
          setArc(top, pair.top, typed);
          setArc(bottom, pair.bottom, typed);
        }
      }
    }
    // ── …AND THE SAME AGAIN FOR THE GRIP PLATE'S THREE GAPS (2026-07-30) ─────────────────────────
    // Identical reasoning, one more slot. Fewer TYPED arcs than the new layout has gaps means the
    // wording has to be REDISTRIBUTED, not copied — the 1:1 migration would leave gaps 2 and 3
    // showing placeholders while everything the buyer wrote sat in gap 1. Joins first, so two typed
    // lines coming from a bumper become one string before being re-cut three ways; that is what
    // keeps a bumper ↔ grip hop from shredding their wording a word at a time.
    if (!crossFamily && !repeated && prevTyped.length && prevTyped.length < 3 && nextArcs.length === 3
        && arcAt('top') && arcAt('left') && arcAt('right')) {
      const joined = [...new Set(prevTyped.map((e) => String(e.text).trim()))].join(' ');
      const tri = joined ? deriveArcTextTri({ name: joined }) : null;
      if (tri) {
        setArc(arcAt('top'), tri.top);
        setArc(arcAt('left'), tri.left);
        setArc(arcAt('right'), tri.right);
      }
    }
    // ── …AND THE MIRROR: TWO LINES INTO ONE ARC MUST JOIN, NOT TRUNCATE ──────────────────────────
    // Pre-existing, and found by round-tripping the fix above. Varsity Arch "BATTERY CREEK / HIGH
    // SCHOOL" → Marquee kept only "BATTERY CREEK": the 1:1 migration pairs the first old arc with the
    // one new arc and the second is simply dropped. `reseedIdentityIntoPlaceholders` DOES know how to
    // join ("The WHOLE identity on one line — the SAME WORDS the two-arc layout shows, joined") but it
    // only fills slots still showing a placeholder, and the migration had already filled this one.
    //   Left unfixed, the two directions disagree and the round trip destroys words: split gave
    // BATTERY CREEK / HIGH SCHOOL, the hop to one arc dropped HIGH SCHOOL, and the hop back re-split
    // what remained into BATTERY / CREEK. Fixing both directions makes the pair lossless — join is the
    // inverse of split, so any number of hops returns the same words.
    //   Deduped, because the twin-arch layouts legitimately paint the SAME line in both slots and
    // joining that would print the name twice — the same case reseedIdentityIntoPlaceholders guards.
    //
    //   ⚠ AND IT WAS THE FIRST FALSEHOOD IN RYAN'S 2026-08-02 REPORT. Keyed on `prevWorded`, this is
    // the one rule that fires on a plate whose two arches the app DERIVED — DENVER / TIGERS off an
    // organisation name — and it was stamping the joined line `textUserSet`. From that hop on the
    // design was no longer a view of its identity: the line rode the bumper's four top-only and three
    // bottom-only layouts as "typed", was remembered as such, and came back on both arches. The join
    // itself is right and stays; only the provenance was wrong. See `setArc`.
    if (!crossFamily && prevWorded.length === 2 && nextArcs.length === 1) {
      const words = [...new Set(prevWorded.map((e) => String(e.text).trim()))];
      const joined = words.join(' ');
      setArc(nextArcs[0], joined, prevWorded.some((e) => e.textUserSet === true));
    }
    // ── THE SAME MIRROR, FOR THE COUNTS THE GRIP PLATE MADE REACHABLE (2026-07-30) ───────────────
    // The rule above is written as a literal 2 → 1 because until now those were the only counts that
    // existed. A three-gap layout going back to the bumper's Varsity Arch is 3 → 2, which that test
    // simply lets through — so the third gap's words were dropped on the floor by the 1:1 migration
    // with nothing to catch them. Join everything typed, then re-cut it to whatever the destination
    // actually has, so a hop is lossless at EVERY combination of counts rather than at the two that
    // happened to be implemented.
    //   Skips the pair case above rather than re-doing it: both produce the same string, and leaving
    // exactly one rule owning each transition is what keeps this debuggable.
    if (!crossFamily && !repeated && prevTyped.length > nextArcs.length && nextArcs.length >= 1
        && !(prevWorded.length === 2 && nextArcs.length === 1)) {
      const joined = [...new Set(prevTyped.map((e) => String(e.text).trim()))].join(' ');
      if (joined && nextArcs.length === 1) setArc(nextArcs[0], joined);
      else if (joined && nextArcs.length === 2) {
        const pair = deriveArcText({ name: joined });
        const top = arcAt('top') || nextArcs[0];
        const bottom = arcAt('bottom') || nextArcs[1];
        if (pair.top && pair.bottom) { setArc(top, pair.top); setArc(bottom, pair.bottom); }
      }
    }
  }

  // Seed any empty logo slot in the new layout from the persistent saved logo — so
  // a plate shares ONE logo across all its slots AND recovers the logo when a logo
  // layout reappears after a text-only one.
  const saved = profile.savedLogo;
  if (saved && saved.dataUrl) {
    next.forEach((ne) => {
      if (ne.type === 'logo' && (!ne.logo || !ne.logo.dataUrl)) ne.logo = { ...saved };
    });
  }
  // Every logo slot wears the same mark, so every logo slot wears the same recolour. Only fills slots
  // the migration above didn't already carry it into — one plate, one paint job.
  const savedSwaps = profile.savedLogoToneSwaps;
  if (Array.isArray(savedSwaps) && savedSwaps.length) {
    next.forEach((ne) => {
      if (ne.type === 'logo' && !(Array.isArray(ne.logoToneSwaps) && ne.logoToneSwaps.length)) {
        ne.logoToneSwaps = savedSwaps.map((s) => ({ ...s }));
      }
    });
  }
  // ── THE DESTINATION WEARS ITS OWN SIZES (Ryan 2026-07-30) — see the save block above ──────────
  // Runs LAST, so it has the final say over `migrateElementSize`, which ran during the migration and
  // deliberately carried the source's numbers across. That carry is right between two layouts of ONE
  // product (Ryan 2026-07-29: *"logo on the left at 90%, change to a layout with the logo on top and
  // bottom — should the % stay at 90%?"* — yes) and is exactly the leak between two PRODUCTS. Same
  // shape as the wording fix directly above it: keep the within-family carry, break the cross-family
  // one.
  //
  // ⚠ CLEARING `sizeUserSet` IS THE HALF THAT ACTUALLY DECOUPLES. `setLayoutPreset` re-seeds the
  // defaults immediately after this returns, and that re-seed skips anything flagged hand-sized — so
  // carrying the flag across is what pinned the dumbbell to the bumper's number. Restoring the
  // pristine `sizeScale` alongside it matters for the same reason `migrateElementSize` insists the
  // two travel together: a stale multiplier left behind with no flag would be silently adopted by
  // any piece the seeder happens not to touch.
  if (crossFamily) {
    const mine = savedSizesFor(profile, destFamily);
    next.forEach((ne) => {
      const remembered = rememberedSizeFor(mine, ne);
      if (Number.isFinite(remembered)) { ne.sizeScale = remembered; ne.sizeUserSet = true; return; }
      const seeded = pristineSize.get(ne);
      if (Number.isFinite(seeded)) ne.sizeScale = seeded; else delete ne.sizeScale;
      delete ne.sizeUserSet;
    });
  }
  profile.layoutPreset = preset.id;
  profile.elements = next;
  return profile;
}
